import { useMemo } from "react";
import { BaseClickableIcon } from "../../../../../components/Icon/BaseClickableIcon.tsx";
import { useAppMutation } from "../../../../../http/useAppMutation.ts";
import { useDebugMode } from "../../../../components/DebugMode/hooks/useDebugMode.ts";
import { useUser } from "../../../../hooks/useUser.ts";
import { isUserAllowedTo } from "../../../../utils/isUserAllowedTo.ts";
import { PanelSection } from "../../../components/Panel/PanelSection.tsx";
import { FAVORITE_TAG_NAME } from "../../../constants.ts";
import { ContentDownloadButton } from "../../../ContentDownload/ContentDownloadButton.tsx";
import { useBoard } from "../../../hooks/useBoard.ts";
import { useDeleteAssetOnBoard } from "../../../hooks/useDeleteAssetOnBoard.ts";
import { useSelectedAsset } from "../../../hooks/useSelectedAsset.ts";
import { useUpdateContent } from "../../../hooks/useUpdateContent.ts";

export const SelectedAssetActions = () => {
  const isDebugModeOn = useDebugMode();
  const { board } = useBoard();
  const { selectedAsset, isLoading: isAssetLoading } = useSelectedAsset();

  const favoriteTag = useMemo(
    () => board.tags.find((it) => it.name === FAVORITE_TAG_NAME),
    [board.tags],
  );
  const addTag = useAppMutation({
    path: "asset-tags/add-assets",
    invalidate: selectedAsset
      ? [`assets/${selectedAsset.uuid}`, `boards/${board.uuid}/assets`]
      : [`boards/${board.uuid}/assets`],
  }).mutation;
  const removeTag = useAppMutation({
    path: "asset-tags/remove-assets",
    invalidate: selectedAsset
      ? [`assets/${selectedAsset.uuid}`, `boards/${board.uuid}/assets`]
      : [`boards/${board.uuid}/assets`],
  }).mutation;

  const addOrRemoveFavoriteTag = () => {
    if (!favoriteTag) return;
    if (selectedAsset?.is_in_favorite) {
      removeTag.mutate({
        asset_uuids: [selectedAsset.uuid],
        tag_uuid: favoriteTag.uuid,
      });
    } else {
      addTag.mutate({
        asset_uuids: [selectedAsset?.uuid],
        tag_uuid: favoriteTag.uuid,
      });
    }
  };
  const { user } = useUser();
  const { updateContent } = useUpdateContent();

  const { mutate: deleteAssetMutation, isLoading: deleteAssetIsLoading } =
    useDeleteAssetOnBoard();

  return (
    <PanelSection className="items-end">
      <div className="flex-row items-center gap-100">
        {favoriteTag && (
          <BaseClickableIcon
            name="Heart"
            size="sm"
            iconClassName={
              selectedAsset?.is_in_favorite
                ? "fill-error-rest stroke-error-rest"
                : ""
            }
            tooltip={{
              side: "bottom",
              content: selectedAsset?.is_in_favorite
                ? "Remove from favorites"
                : "Add to favorites",
            }}
            onClick={() => {
              addOrRemoveFavoriteTag();
            }}
            className="p-200 rounded-100 hover:bg-surface-primary-hover"
          />
        )}
        <div className="h-400 border-l-025 border-primary-rest" />
        <BaseClickableIcon
          name="Archive"
          size="sm"
          tooltip={{ side: "bottom", content: "Archive" }}
          disabled={
            deleteAssetIsLoading ||
            (!isAssetLoading && selectedAsset?.deleted_at !== null)
          }
          onClick={() => {
            if (selectedAsset) {
              deleteAssetMutation({
                uuids: [selectedAsset.uuid],
              });
            }
          }}
          className="p-200 rounded-100 hover:bg-surface-primary-hover"
        />
        <ContentDownloadButton asset={selectedAsset} />
      </div>
      {user &&
        isUserAllowedTo(user, "contents:curate") &&
        isDebugModeOn &&
        selectedAsset?.content.uuid && (
          <BaseClickableIcon
            name="Lightbulb"
            size="sm"
            iconClassName={
              selectedAsset.content.is_curated
                ? "fill-surface-caution-primary-rest stroke-caution-rest"
                : ""
            }
            tooltip={{
              side: "bottom",
              content: selectedAsset.content.is_curated
                ? "Remove from inspiration library"
                : "Add to inspiration library",
            }}
            onClick={() => {
              updateContent({
                contentUuid: selectedAsset.content.uuid,
                isCurated: !selectedAsset.content.is_curated,
                linkedAssetUuid: selectedAsset.uuid,
              });
            }}
            className="p-200 rounded-100 hover:bg-surface-primary-hover"
          />
        )}
    </PanelSection>
  );
};
